import {
  CalendarEvent,
  ICalendarEvent,
  ICalendarEventJS,
} from '@app/models/CalendarEvent'
import { ApplicationState } from '@app/store'
import { errorToast } from '@app/utils/notifications'
import { makeAuthenticatedRequest } from '@app/utils/request'
import { List, Record } from 'immutable'
import moment from 'moment'
import { Action, Reducer } from 'redux'
import { fork, put, select, takeLatest } from 'redux-saga/effects'
import { createSelector } from 'reselect'

// -------------------------
// STATE
// -------------------------
export enum ListingsCalendarScope {
  All = 'All',
  Mine = 'Mine',
}

export interface IListingsCalendarState {
  readonly downloadUrl: string
  readonly endTime: number
  readonly error?: string
  readonly events: List<ICalendarEvent>
  readonly isLoading: boolean
  readonly scope: ListingsCalendarScope
  readonly startTime: number
}

const ListingsCalendarStateRecord = Record({
  downloadUrl: '',
  endTime: 0,
  error: undefined,
  events: List<CalendarEvent>(),
  isLoading: false,
  scope: ListingsCalendarScope.Mine,
  startTime: 0,
})

export class ListingsCalendarState extends ListingsCalendarStateRecord
  implements IListingsCalendarState {
  public static fromJS(initialState?: Partial<IListingsCalendarState>) {
    if (!initialState) {
      return new ListingsCalendarState()
    }

    const { events, ...rest } = initialState

    return new ListingsCalendarState({
      events: events
        ? List(events.map((e) => new CalendarEvent(e)))
        : List<CalendarEvent>(),
      ...rest,
    })
  }

  public readonly downloadUrl: string
  public readonly endTime: number
  public readonly error?: string
  public readonly events: List<CalendarEvent>
  public readonly isLoading: boolean
  public readonly scope: ListingsCalendarScope
  public readonly startTime: number

  constructor(props?: Partial<ListingsCalendarState>) {
    props ? super(props) : super()
  }
}

// -------------------------
// ACTIONS
// -------------------------

export enum ListingsCalendarActionTypes {
  LoadEvents = 'LISTINGS_CALENDAR::LOAD_EVENTS',
  LoadEventsFailure = 'LISTINGS_CALENDAR::LOAD_EVENTS_FAILURE',
  LoadEventsRequest = 'LISTINGS_CALENDAR::LOAD_EVENTS_REQUEST',
  LoadEventsSuccess = 'LISTINGS_CALENDAR::LOAD_EVENTS_SUCCESS',
  SetScope = 'LISTINGS_CALENDAR::SET_SCOPE',
}

interface ILoadEventsAction {
  end: number
  start: number
  type: ListingsCalendarActionTypes.LoadEvents
}

interface ILoadEventsFailureAction {
  error: string
  type: ListingsCalendarActionTypes.LoadEventsFailure
}

interface ILoadEventsRequestAction {
  end: number
  start: number
  type: ListingsCalendarActionTypes.LoadEventsRequest
}

interface ILoadEventsSuccessAction {
  downloadUrl: string
  events: CalendarEvent[]
  type: ListingsCalendarActionTypes.LoadEventsSuccess
}

interface ISetScopeAction {
  scope: ListingsCalendarScope
  type: ListingsCalendarActionTypes.SetScope
}

// Declare a 'discriminated union' type. This guarantees that all references to 'type' properties contain one of the
// declared type strings (and not any other arbitrary string).
type KnownAction =
  | ILoadEventsFailureAction
  | ILoadEventsRequestAction
  | ILoadEventsSuccessAction
  | ISetScopeAction

// -------------------------
// ACTION CREATORS
// -------------------------

function loadEvents(start: number, end: number): ILoadEventsAction {
  return {
    end,
    start,
    type: ListingsCalendarActionTypes.LoadEvents,
  }
}

function loadEventsFailure(error: string): ILoadEventsFailureAction {
  return {
    error,
    type: ListingsCalendarActionTypes.LoadEventsFailure,
  }
}

function loadEventsRequest(
  start: number,
  end: number,
): ILoadEventsRequestAction {
  return {
    end,
    start,
    type: ListingsCalendarActionTypes.LoadEventsRequest,
  }
}

function loadEventsSuccess(
  events: CalendarEvent[],
  downloadUrl: string,
): ILoadEventsSuccessAction {
  return {
    downloadUrl,
    events,
    type: ListingsCalendarActionTypes.LoadEventsSuccess,
  }
}

function setScope(scope: ListingsCalendarScope): ISetScopeAction {
  return {
    scope,
    type: ListingsCalendarActionTypes.SetScope,
  }
}

export const actionCreators = {
  loadEvents,
  setScope,
}

// -------------------------
// REDUCER
// -------------------------
const initState = new ListingsCalendarState()

export const reducer: Reducer<ListingsCalendarState> = (
  state: ListingsCalendarState = initState,
  incomingAction: Action,
) => {
  const action = incomingAction as KnownAction
  switch (action.type) {
    case ListingsCalendarActionTypes.LoadEventsFailure:
      return state
        .set('error', undefined)
        .set('isLoading', false) as ListingsCalendarState

    case ListingsCalendarActionTypes.LoadEventsRequest:
      return state
        .set('startTime', action.start)
        .set('endTime', action.end)
        .set('isLoading', true) as ListingsCalendarState

    case ListingsCalendarActionTypes.LoadEventsSuccess:
      return state
        .set('downloadUrl', action.downloadUrl)
        .set('events', List(action.events))
        .set('isLoading', false) as ListingsCalendarState

    case ListingsCalendarActionTypes.SetScope:
      return state.set('scope', action.scope) as ListingsCalendarState

    default:
      // The following line guarantees that every action in the KnownAction union has been covered by a case above
      const exhaustiveCheck: never = action
  }

  return state
}

// -------------------------
// SAGAS
// -------------------------
function* loadEventsSaga(action: ILoadEventsAction) {
  try {
    yield put(loadEventsRequest(action.start, action.end))

    const scope: ListingsCalendarScope = yield select(selectScope())

    const query = [
      `start=${encodeURIComponent(moment.unix(action.start).toISOString())}`,
      `end=${encodeURIComponent(moment.unix(action.end).toISOString())}`,
    ]

    const baseUrl =
      scope === ListingsCalendarScope.All
        ? '/api/Calendars/Listings'
        : '/api/Calendars/Listings/My'

    const result = yield makeAuthenticatedRequest(
      `${baseUrl}?${query.join('&')}`,
    )

    if (result.success === true) {
      const events = result.results.map((c: ICalendarEventJS) =>
        CalendarEvent.fromJS(c),
      )
      yield put(loadEventsSuccess(events, result.downloadUrl))
    } else {
      yield put(loadEventsFailure(result.message))
      yield put(errorToast('Oops!', result.message))
    }
  } catch (err) {
    yield put(loadEventsFailure(err.message))
    yield put(errorToast('Oops!', err.message))
  }
}

function* watchLoadEvents() {
  yield takeLatest(ListingsCalendarActionTypes.LoadEvents, loadEventsSaga)
}

export function* listingsCalendarRootSaga() {
  yield fork(watchLoadEvents)
}

// -------------------------
// SELECTORS
// -------------------------
const selectModuleState = () => (state: ApplicationState) =>
  state.listingsCalendar

export const selectDownloadUrl = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.downloadUrl,
  )

export const selectEndTime = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.endTime,
  )

export const selectError = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.error,
  )

export const selectEvents = () =>
  createSelector(
    selectModuleState(),
    (mState) => {
      return mState.events
    },
  )

export const selectIsLoading = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.isLoading,
  )

export const selectScope = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.scope,
  )

export const selectStartTime = () =>
  createSelector(
    selectModuleState(),
    (mState) => mState.startTime,
  )
